import React, { useEffect, useState } from "react";
import { Play, Warehouse, ShoppingBag, Target, Gift, Settings, Coins, User, LogIn, Trophy, Crown, Wifi, Timer, Users, X, Sparkles, Medal, Copy, Link2, Check, Dices, Lock, Gamepad2, Boxes } from "lucide-react";
import { VehiclePreview } from "../components/VehiclePreview";
import { WebAd } from "../components/WebAd";
import { usePlayer } from "../context/PlayerContext";
import { api, apiErr } from "../api/client";
import { VEHICLES } from "../game/config";
import { Sound } from "../game/audio";
import { AdManager } from "../game/ads";
import { toast } from "sonner";

const NAV = [
  { id: "garage", label: "Garage", icon: Warehouse, test: "garage-tab" },
  { id: "shop", label: "Shop", icon: ShoppingBag, test: "shop-tab" },
  { id: "battlepass", label: "Battle Pass", icon: Crown, test: "battlepass-tab" },
  { id: "salvage", label: "Salvage", icon: Boxes, test: "salvage-tab" },
  { id: "casino", label: "Casino", icon: Dices, test: "casino-tab" },
  { id: "achievements", label: "Awards", icon: Medal, test: "achievements-tab" },
  { id: "missions", label: "Missions", icon: Target, test: "missions-tab" },
  { id: "daily", label: "Daily", icon: Gift, test: "daily-tab" },
  { id: "settings", label: "Settings", icon: Settings, test: "settings-tab" },
];

export default function MainMenu({ onPlay, onPanel }) {
  const { profile, applyUser } = usePlayer();
  const [lb, setLb] = useState([]);
  const [seasonLb, setSeasonLb] = useState([]);
  const [seasonReset, setSeasonReset] = useState(0);
  const [lbMode, setLbMode] = useState("all");
  const [dailyReady, setDailyReady] = useState(false);
  const [showParty, setShowParty] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [createdCode, setCreatedCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [seasonRewards, setSeasonRewards] = useState([]);
  const [claiming, setClaiming] = useState(false);
  const [mpBusy, setMpBusy] = useState(false);
  const [bpClaimable, setBpClaimable] = useState(0);
  const MP_MIN_LEVEL = 3, MP_COST = 2000, CHAMP_MIN_LEVEL = 8, CHAMP_FEE = 5000;
  const champEligible = profile.accountLevel >= CHAMP_MIN_LEVEL && profile.unlockedMultiplayer;

  useEffect(() => {
    api.leaderboard().then(({ data }) => setLb(data.top || [])).catch(() => {});
    api.seasonLeaderboard().then(({ data }) => { setSeasonLb(data.top || []); setSeasonReset(data.resetInSeconds || 0); }).catch(() => {});
    api.daily().then(({ data }) => setDailyReady(data.canClaim)).catch(() => {});
    api.seasonRewards().then(({ data }) => setSeasonRewards(data.rewards || [])).catch(() => {});
    api.battlepass().then(({ data }) => {
      let n = 0;
      for (let t = 1; t <= data.tierReached; t++) {
        if (!data.claimedFree.includes(t)) n++;
        if (data.premium && !data.claimedPremium.includes(t)) n++;
      }
      setBpClaimable(n);
    }).catch(() => {});
    // Invite deep-link: /?party=CODE auto-opens the join form.
    const p = new URLSearchParams(window.location.search).get("party");
    if (p) { setJoinCode(p.toUpperCase().slice(0, 6)); setShowParty(true); window.history.replaceState(null, "", window.location.pathname); }
  }, [profile]);

  const genCode = () => { const cs = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; let c = ""; for (let i = 0; i < 4; i++) c += cs[Math.floor(Math.random() * cs.length)]; return c; };
  const openParty = () => { setCreatedCode(""); setCopied(false); setShowParty(true); };
  const createParty = () => setCreatedCode(genCode());
  const inviteLink = (code) => `${window.location.origin}/?party=${code}`;
  const copy = async (text) => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { console.warn("copy failed", e); } };
  const startHosted = () => { Sound.init(); Sound.resume(); api.partyHosted().catch(() => {}); onPlay("party", { code: createdCode }); };
  const joinParty = () => { const c = joinCode.trim().toUpperCase(); if (c.length < 3) return; Sound.init(); Sound.resume(); onPlay("party", { code: c }); };
  const [freeBusy, setFreeBusy] = useState(false);
  const watchForCoins = async () => {
    if (freeBusy) return;
    setFreeBusy(true);
    try { await AdManager.playRewarded({ seconds: 3 }); const { data } = await api.freeCoins(); if (data.user) applyUser(data.user); toast.success(`+${data.granted} free coins!`); }
    catch (e) { toast.error(apiErr(e, "Come back a bit later for more free coins.")); }
    finally { setFreeBusy(false); }
  };

  const claimSeason = async () => {
    setClaiming(true);
    try {
      const { data } = await api.claimSeasonRewards();
      if (data.user) applyUser(data.user);
      setSeasonRewards([]);
      toast.success(`Season rewards claimed! +${(data.coins || 0).toLocaleString()} coins`);
      Sound.levelup?.();
    } catch (e) { toast.error(apiErr(e, "Could not claim rewards.")); }
    finally { setClaiming(false); }
  };

  const fmtReset = (s) => {
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600);
    return d > 0 ? `${d}d ${h}h` : `${h}h`;
  };

  if (!profile) return null;
  const theme = (profile.garage?.skin || "skin_classic").replace("skin_", "");
  const tier = profile.maxEvolutionReached || 0;
  const isGuest = profile.accountType === "guest";

  const play = () => { Sound.init(); Sound.resume(); onPlay("online"); };
  const playSolo = () => { Sound.init(); Sound.resume(); onPlay("solo"); };
  const unlockMP = async () => {
    if (profile.accountLevel < MP_MIN_LEVEL) { toast.error(`Reach level ${MP_MIN_LEVEL} to unlock Multiplayer — play Solo to level up!`); return; }
    setMpBusy(true);
    try { const { data } = await api.unlockMultiplayer(); if (data.user) applyUser(data.user); toast.success("Multiplayer Arena unlocked! 🔥"); }
    catch (e) { toast.error(apiErr(e, "Could not unlock.")); }
    finally { setMpBusy(false); }
  };
  const playChampions = async () => {
    if (profile.accountLevel < CHAMP_MIN_LEVEL) { toast.error(`Reach level ${CHAMP_MIN_LEVEL} to enter Champions Arena`); return; }
    if (!profile.unlockedMultiplayer) { toast.error("Unlock Multiplayer first"); return; }
    if (profile.coins < CHAMP_FEE) { toast.error(`Champions entry fee is ${CHAMP_FEE.toLocaleString()} coins`); return; }
    setMpBusy(true);
    try { const { data } = await api.enterChampions(); if (data.user) applyUser(data.user); Sound.init(); Sound.resume(); toast.success("Welcome to Champions Arena — 3× rewards!"); onPlay("champions"); }
    catch (e) { toast.error(apiErr(e, "Could not enter Champions.")); }
    finally { setMpBusy(false); }
  };

  return (
    <div className="arena-bg grain relative min-h-screen w-full overflow-y-auto scroll-thin" data-testid="main-menu">
      {/* Account bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-3 sm:p-4 z-10">
        <button
          onClick={() => onPanel(isGuest ? "auth" : "settings")}
          className="card px-3 py-2 flex items-center gap-2 pointer-events-auto hover:border-amber-500/50"
          data-testid="account-status"
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center">
            <User size={16} className="text-black" />
          </div>
          <div className="text-left">
            <div className="text-sm font-bold text-white leading-none">{profile.displayName}</div>
            <div className={`text-[10px] font-semibold ${isGuest ? "text-amber-400" : "text-green-400"}`}>
              {isGuest ? "GUEST — SAVE PROGRESS" : profile.accountType === "google" ? "GOOGLE CONNECTED ✓" : "ACCOUNT ✓"}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <div className="card px-3 py-2 flex items-center gap-1.5" data-testid="coins-display">
            <Coins size={16} className="text-amber-400" />
            <span className="font-black text-white">{profile.coins.toLocaleString()}</span>
          </div>
          <div className="card px-3 py-2 hidden sm:flex items-center gap-1.5">
            <span className="text-[10px] text-slate-400 uppercase font-bold">Lvl</span>
            <span className="font-black text-cyan-400">{profile.accountLevel}</span>
          </div>
        </div>
      </div>

      <div className="min-h-screen flex flex-col lg:flex-row items-center justify-center gap-6 px-4 py-20 max-w-6xl mx-auto">
        {/* Left: brand + play */}
        <div className="flex-1 text-center lg:text-left">
          <div className="inline-block badge-glow text-xs font-black uppercase tracking-widest text-amber-400 border border-amber-500/40 rounded-full px-3 py-1 mb-3">Destruction Arena</div>
          <h1 className="font-head font-black uppercase tracking-tighter leading-[0.85] text-6xl sm:text-7xl lg:text-8xl">
            <span className="text-white">SMASH</span><span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-600">UP</span>
            <span className="text-cyan-400">.io</span>
          </h1>
          <p className="text-slate-400 mt-3 text-base sm:text-lg max-w-md mx-auto lg:mx-0">
            Drive. Smash everything. Collect. Evolve into an unstoppable machine. Dominate the arena.
          </p>

          {profile.unlockedMultiplayer ? (
            <button onClick={play} className="btn-primary text-2xl sm:text-3xl px-12 py-4 mt-6 flex items-center gap-3 mx-auto lg:mx-0 glow-pulse" data-testid="play-button">
              <Play size={28} fill="currentColor" /> PLAY ONLINE
            </button>
          ) : (
            <button onClick={unlockMP} disabled={mpBusy} className="btn-gold text-lg sm:text-2xl px-8 py-4 mt-6 flex items-center gap-3 mx-auto lg:mx-0 disabled:opacity-60" data-testid="unlock-multiplayer-button">
              <Lock size={22} /> {profile.accountLevel < MP_MIN_LEVEL ? `MULTIPLAYER — REACH LVL ${MP_MIN_LEVEL}` : `UNLOCK MULTIPLAYER · ${MP_COST.toLocaleString()}`}
            </button>
          )}
          <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1.5 justify-center lg:justify-start" data-testid="online-hint">
            <Wifi size={13} className="text-cyan-400" /> {profile.unlockedMultiplayer ? "Live arena — real players & smart rivals" : `Unlocks at level ${MP_MIN_LEVEL}. Practice solo to level up!`}
          </div>
          <div className="flex flex-wrap gap-2 mt-3 justify-center lg:justify-start">
            <button onClick={playSolo} className="btn-ghost text-base px-6 py-2.5 flex items-center gap-2 border-cyan-500/40 text-cyan-300" data-testid="solo-play-button">
              <Gamepad2 size={18} /> SOLO PRACTICE
            </button>
            <button onClick={playChampions} disabled={!champEligible || mpBusy} className={`text-base px-6 py-2.5 rounded-xl font-black uppercase tracking-wide flex items-center gap-2 border-2 transition-transform ${champEligible ? "btn-vip border-transparent" : "border-slate-700 text-slate-500 bg-slate-900/60 cursor-not-allowed"}`} data-testid="champions-button" title={champEligible ? `Entry fee ${CHAMP_FEE.toLocaleString()} coins · 3× rewards` : `Unlocks at level ${CHAMP_MIN_LEVEL}`}>
              <Crown size={18} /> {profile.accountLevel < CHAMP_MIN_LEVEL ? `CHAMPIONS · LVL ${CHAMP_MIN_LEVEL}` : `CHAMPIONS · ${CHAMP_FEE.toLocaleString()}`}
            </button>
          </div>
          <button onClick={openParty} className="btn-ghost text-base px-8 py-2.5 mt-3 flex items-center gap-2 mx-auto lg:mx-0 border-cyan-500/40 text-cyan-300" data-testid="party-play-button">
            <Users size={18} /> PLAY WITH FRIENDS (Party)
          </button>
          <button onClick={watchForCoins} disabled={freeBusy} className="btn-ghost text-sm px-6 py-2 mt-2 flex items-center gap-2 mx-auto lg:mx-0 border-amber-500/40 text-amber-300 disabled:opacity-50" data-testid="free-coins-button">
            <Gift size={16} /> {freeBusy ? "Loading ad..." : "FREE COINS (Watch Ad)"}
          </button>

          {seasonRewards.length > 0 && (
            <div className="card mt-4 p-3 border-2 border-amber-500/60 glow-pulse flex items-center justify-between gap-3 max-w-md mx-auto lg:mx-0" data-testid="season-reward-banner">
              <div className="flex items-center gap-2">
                <Crown size={22} className="text-amber-400" />
                <div className="text-left">
                  <div className="text-white font-black text-sm">Season Rewards Ready!</div>
                  <div className="text-[11px] text-slate-300">{seasonRewards[0].label} · +{seasonRewards.reduce((a, r) => a + (r.coins || 0), 0).toLocaleString()} coins{seasonRewards.some((r) => (r.cosmeticNames || []).length) ? ` + ${seasonRewards.flatMap((r) => r.cosmeticNames || []).join(", ")}` : ""}</div>
                </div>
              </div>
              <button disabled={claiming} onClick={claimSeason} className="btn-primary py-2 px-4 text-sm shrink-0" data-testid="claim-season-reward-btn">{claiming ? "..." : "Claim"}</button>
            </div>
          )}

          <div className="flex items-center gap-3 mt-4 justify-center lg:justify-start text-sm text-slate-400">
            <Trophy size={16} className="text-amber-400" />
            Personal Best: <span className="text-white font-bold">{(profile.statistics?.personalBest || 0).toLocaleString()}</span>
          </div>

          {/* Battle Pass claim banner */}
          {bpClaimable > 0 && (
            <button onClick={() => onPanel("battlepass")} className="card mt-4 p-3 border-2 border-amber-500/60 glow-pulse flex items-center justify-between gap-3 max-w-md mx-auto lg:mx-0 w-full hover:-translate-y-0.5 transition-transform" data-testid="bp-banner">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center shrink-0"><Crown size={22} className="text-black" /></div>
                <div className="text-left">
                  <div className="text-white font-black text-sm">Battle Pass rewards ready!</div>
                  <div className="text-[11px] text-amber-300 font-bold">{bpClaimable} reward{bpClaimable > 1 ? "s" : ""} waiting to claim</div>
                </div>
              </div>
              <span className="btn-primary py-1.5 px-3 text-xs shrink-0">Claim</span>
            </button>
          )}

          {/* Nav */}
          <div className="grid grid-cols-3 gap-2 mt-6 max-w-md mx-auto lg:mx-0">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => onPanel(n.id)} className="card py-3 flex flex-col items-center gap-1 hover:border-amber-500/50 hover:-translate-y-1 transition-all relative" data-testid={n.test}>
                <n.icon size={20} className="text-cyan-400" />
                <span className="text-[10px] font-bold uppercase text-slate-300">{n.label}</span>
                {n.id === "daily" && dailyReady && <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />}
                {n.id === "battlepass" && bpClaimable > 0 && <span className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-amber-500 text-black text-[9px] font-black flex items-center justify-center animate-pulse" data-testid="bp-nav-badge">{bpClaimable}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Right: vehicle showcase + leaderboard */}
        <div className="flex-1 flex flex-col items-center gap-4 w-full max-w-sm">
          <div className="card w-full p-5 flex flex-col items-center relative overflow-hidden">
            <div className="absolute inset-0 spin-slow opacity-20" style={{ background: "conic-gradient(from 0deg, transparent, rgba(255,149,0,0.3), transparent)" }} />
            <div className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-1 z-10">{VEHICLES[tier].name}</div>
            <VehiclePreview tier={tier} theme={theme} size={220} />
            <div className="text-slate-400 text-xs z-10">Max Evolution Reached</div>
          </div>

          <div className="card w-full p-3" data-testid="global-leaderboard">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-cyan-400 font-bold uppercase text-xs tracking-widest px-1">
                <Crown size={14} /> {lbMode === "all" ? "Top Smashers" : "This Week"}
              </div>
              <div className="flex gap-1">
                <button onClick={() => setLbMode("all")} className={`text-[10px] font-bold uppercase px-2 py-1 rounded-lg ${lbMode === "all" ? "btn-primary" : "bg-slate-800 text-slate-400"}`} data-testid="lb-all-time">All-Time</button>
                <button onClick={() => setLbMode("week")} className={`text-[10px] font-bold uppercase px-2 py-1 rounded-lg ${lbMode === "week" ? "btn-primary" : "bg-slate-800 text-slate-400"}`} data-testid="lb-weekly">Weekly</button>
              </div>
            </div>
            {lbMode === "week" && (
              <div className="flex items-center gap-1 text-[10px] text-amber-400 font-semibold px-1 mb-1" data-testid="season-reset">
                <Timer size={11} /> Season resets in {fmtReset(seasonReset)}
              </div>
            )}
            {(lbMode === "all" ? lb : seasonLb).length === 0 && <div className="text-slate-500 text-sm px-1 py-2">{lbMode === "all" ? "Be the first to set a score!" : "No scores yet this week — claim the crown!"}</div>}
            {(lbMode === "all" ? lb : seasonLb).slice(0, 5).map((e, i) => (
              <div key={e.userId || `${e.displayName}-${i}`} className="flex items-center gap-2 px-1 py-1 text-sm">
                <span className={`w-5 font-black ${i === 0 ? "text-amber-400" : "text-slate-500"}`}>{i + 1}</span>
                <span className="flex-1 truncate text-slate-300 font-semibold">{e.displayName}</span>
                <span className="text-white font-mono font-bold">{e.score.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {!profile.adsRemoved && (
        <div className="pb-8 px-4" data-testid="menu-ad-banner">
          <WebAd format="leaderboard" />
        </div>
      )}

      {showParty && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="party-modal" onClick={() => setShowParty(false)}>
          <div className="card w-full max-w-sm p-5 pop-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-cyan-400 font-head font-black text-xl uppercase"><Users size={20} /> Party Play</div>
              <button onClick={() => setShowParty(false)} className="text-slate-400 hover:text-white" data-testid="party-close"><X size={20} /></button>
            </div>
            <p className="text-slate-400 text-sm mb-4">Start a private arena and share the code or link with friends — up to 8 can drop in together.</p>

            {!createdCode ? (
              <button onClick={createParty} className="btn-primary w-full py-3 flex items-center justify-center gap-2 mb-4" data-testid="party-host-btn">
                <Sparkles size={18} /> CREATE PARTY
              </button>
            ) : (
              <div className="card p-3 mb-4 border-amber-500/40" data-testid="party-created">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold text-center mb-1">Your Party Code</div>
                <div className="font-head font-black text-4xl text-amber-400 tracking-[0.3em] text-center mb-3" data-testid="party-created-code">{createdCode}</div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <button onClick={() => copy(createdCode)} className="btn-ghost py-2 text-xs flex items-center justify-center gap-1" data-testid="party-copy-code">
                    {copied ? <Check size={13} className="text-green-400" /> : <Copy size={13} />} Copy Code
                  </button>
                  <button onClick={() => copy(inviteLink(createdCode))} className="btn-ghost py-2 text-xs flex items-center justify-center gap-1" data-testid="party-copy-link">
                    {copied ? <Check size={13} className="text-green-400" /> : <Link2 size={13} />} Copy Link
                  </button>
                </div>
                <button onClick={startHosted} className="btn-primary w-full py-2.5 flex items-center justify-center gap-2" data-testid="party-start-btn">
                  <Play size={16} fill="currentColor" /> START PARTY
                </button>
              </div>
            )}

            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-px bg-slate-700" />
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">or join a code</span>
              <div className="flex-1 h-px bg-slate-700" />
            </div>

            <div className="flex gap-2">
              <input
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
                onKeyDown={(e) => { if (e.key === "Enter") joinParty(); }}
                placeholder="ENTER CODE"
                maxLength={6}
                className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-white font-head font-black tracking-widest text-center uppercase focus:border-cyan-500 outline-none"
                data-testid="party-code-input"
              />
              <button onClick={joinParty} disabled={joinCode.trim().length < 3} className="btn-ghost px-5 py-2.5 border-cyan-500/40 text-cyan-300 disabled:opacity-40" data-testid="party-join-btn">Join</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
